import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import { BackButton, PrimaryButton, SecondaryButton } from '../../../../component/buttonComponent/ButtonComponent';
import ColoredLine from '../../../../component/coloredLine/ColoredLine';
import Title from '../../../../component/title/Title';
import Modal from '../../../../component/modal/Modal';
import { APICreateVehicleAdminPreset } from '../../../../config/restAPI/PostAPI';
import { APIUpdateVehicleAdminPreset } from '../../../../config/restAPI/PatchAPI';
import { APIDeleteVehicleAdminPresets } from '../../../../config/restAPI/DeleteAPI';
import { APIGetBatteryPolicies, APIGetFuelPolicies } from '../../../../config/restAPI/FetchAPI';
import {
    createEmptyVehiclePolicyValues,
    createVehiclePresetPayload,
    mapVehiclePresetToFieldValues,
    resolveExcusableIdlingEnabledForDisplay,
    mapPolicyListToDropdownOptions,
    findPolicyOptionById,
    resolveBatteryPolicyIdFromPreset,
    resolveFuelPolicyIdFromPreset,
} from '../vehiclePolicyConfig';
import { VehiclePresetFormGrid } from '../VehiclePresetFormGrid';
import { useRegistryFuelTariffOptions } from '../useRegistryFuelTariffOptions';
import { useVehicleTypeOptions } from '../useVehicleTypeOptions';
import { FUEL_TYPE_FIELD_KEY } from '../vehiclePolicyConfig';
import {
    mapFuelPolicyToVehiclePresetFieldValues,
    findFuelPolicyRecordById,
} from '../fuelPolicyGnssMapping';
import {
    buildHardwareBindingFuelPoliciesParams,
    filterActiveFuelPolicies,
    parseFuelPoliciesListResponse,
} from '../../fuelConfigurator/fuelPolicyApiMapping';
import '../../batteryConfigurator/addBatteryConfig/addBatteryConfig.css';
import '../../assetsDevice/configuration/General/Configuration.css';
import './addVehicleConfig.css';

const AddVehicleConfig = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const token = Cookies.get('jwtToken');
    const agencyId = Cookies.get('agencyUUID');
    const [loading, setLoading] = useState(false);
    const [snErr, setSNErr] = useState(false);
    const [SNErrMessage, setSNErrMessage] = useState('');
    const [presetName, setPresetName] = useState('');
    const [vehicleType, setVehicleType] = useState('');
    const [descriptionValue, setDescriptionValue] = useState('');
    const [fieldValues, setFieldValues] = useState(createEmptyVehiclePolicyValues());
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPolicyId, setCurrentPolicyId] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [fuelPolicyOptions, setFuelPolicyOptions] = useState([]);
    const [fuelPolicyRecords, setFuelPolicyRecords] = useState([]);
    const [selectedFuelPolicy, setSelectedFuelPolicy] = useState(null);
    const lastAppliedFuelPolicyIdRef = useRef(null);
    const [batteryPolicyOptions, setBatteryPolicyOptions] = useState([]);
    const [selectedBatteryPolicy, setSelectedBatteryPolicy] = useState(null);
    const {
        fuelTariffOptions,
        priceByValue: registryPriceByValue,
        validValues: registryValidValues,
        loading: registryOptionsLoading,
    } = useRegistryFuelTariffOptions();
    const {
        vehicleTypeOptions,
        loading: vehicleTypesLoading,
        vehicleClassByName,
        addVehicleType,
    } = useVehicleTypeOptions();
    // Only System Administrators may add a vehicle type; the API rejects anyone else with 403.
    const canManageVehicleTypes = Cookies.get('role') === 'System Administrator';

    const validFuelTariffValues = useMemo(() => {
        const set = new Set(registryValidValues);
        const current = fieldValues[FUEL_TYPE_FIELD_KEY];
        if (current) set.add(current);
        return set;
    }, [registryValidValues, fieldValues]);

    const fetchHardwarePolicyOptions = useCallback(async () => {
        const listParams = { pageNumber: 1, totalPerPage: 500 };
        const noopSetLoading = () => {};
        try {
            const [fuelResult, batteryResult] = await Promise.all([
                APIGetFuelPolicies(noopSetLoading, token, agencyId, buildHardwareBindingFuelPoliciesParams()),
                APIGetBatteryPolicies(noopSetLoading, token, agencyId, listParams),
            ]);

            // Mirror the Fuel Configurator parser: handles stringified `data`,
            // a `fuelPolicies` key, or a bare-array response shape.
            const { policies: fuelPolicies } = parseFuelPoliciesListResponse(fuelResult);
            const activeFuelPolicies = filterActiveFuelPolicies(fuelPolicies);
            setFuelPolicyRecords(activeFuelPolicies);
            setFuelPolicyOptions(mapPolicyListToDropdownOptions(activeFuelPolicies));

            const batteryStatus = batteryResult?.status || batteryResult?.data?.status;
            const batteryList = batteryResult?.data?.data;
            if (batteryStatus === 200 && Array.isArray(batteryList)) {
                setBatteryPolicyOptions(mapPolicyListToDropdownOptions(batteryList));
            }
        } catch (err) {
            // Policy list is optional for save; ignore fetch errors.
        }
    }, [token, agencyId]);

    useEffect(() => {
        if (token && agencyId) {
            fetchHardwarePolicyOptions();
        }
    }, [token, agencyId, fetchHardwarePolicyOptions]);

    useEffect(() => {
        if (state && state.id) {
            setIsEditMode(true);
            setCurrentPolicyId(state.id);
            setPresetName(state.presetName || '');
            // Keep whatever the preset stores, even if it is not in the current active list — the type
            // may have been deactivated since, and blanking it would silently discard a valid saved
            // classification. VehiclePresetFormGrid merges the stored value into the dropdown options.
            setVehicleType((state.vehicleType || '').trim());
            setDescriptionValue(state.description || '');
            setFieldValues(resolveExcusableIdlingEnabledForDisplay(
                mapVehiclePresetToFieldValues(state),
                state.vehicleType,
                state.vehicleClass,
            ));
            // Preset already stores fuel tank overrides; do not re-apply linked policy on load.
            lastAppliedFuelPolicyIdRef.current = resolveFuelPolicyIdFromPreset(state) || null;
        }
    }, [state]);

    useEffect(() => {
        const fuelMatch = findPolicyOptionById(
            fuelPolicyOptions,
            resolveFuelPolicyIdFromPreset(state || {}),
        );
        if (fuelMatch) {
            setSelectedFuelPolicy(fuelMatch);
        }

        const batteryMatch = findPolicyOptionById(
            batteryPolicyOptions,
            resolveBatteryPolicyIdFromPreset(state || {}),
        );
        if (batteryMatch) {
            setSelectedBatteryPolicy(batteryMatch);
        }
    }, [state, fuelPolicyOptions, batteryPolicyOptions]);

    const requiredFormCompleted = useMemo(() => (
        presetName.trim().length > 0 && vehicleType.trim().length > 0
    ), [presetName, vehicleType]);

    const applyFieldsFromFuelPolicyId = useCallback((policyId) => {
        if (!policyId) {
            lastAppliedFuelPolicyIdRef.current = null;
            return;
        }
        if (lastAppliedFuelPolicyIdRef.current === policyId) {
            return;
        }
        const policy = findFuelPolicyRecordById(fuelPolicyRecords, policyId);
        if (!policy) {
            return;
        }
        const patch = mapFuelPolicyToVehiclePresetFieldValues(policy);
        if (Object.keys(patch).length === 0) {
            return;
        }
        lastAppliedFuelPolicyIdRef.current = policyId;
        setFieldValues((prev) => ({
            ...prev,
            ...patch,
        }));
    }, [fuelPolicyRecords]);

    const handleFuelPolicyChange = useCallback((option) => {
        setSelectedFuelPolicy(option ?? null);
        // User explicitly picked a policy — always prefill from it (even re-selecting the same one).
        lastAppliedFuelPolicyIdRef.current = null;
        applyFieldsFromFuelPolicyId(option?.value ?? null);
    }, [applyFieldsFromFuelPolicyId]);

    const handleFieldChange = (fieldKey, value) => {
        setFieldValues((prev) => ({
            ...prev,
            [fieldKey]: value,
        }));
    };

    const handleFieldValuesPatch = useCallback((patch) => {
        if (!patch || typeof patch !== 'object') return;
        setFieldValues((prev) => ({
            ...prev,
            ...patch,
        }));
    }, []);

    const handleBackButton = () => {
        navigate('/inventory/vehicleconfigurator', { replace: true });
    };

    const handleCreateOrUpdatePolicy = async () => {
        const payload = createVehiclePresetPayload(
            presetName,
            vehicleType,
            descriptionValue,
            fieldValues,
            isEditMode ? currentPolicyId : null,
            {
                fuelPolicyId: selectedFuelPolicy?.value || null,
                batteryPolicyId: selectedBatteryPolicy?.value || null,
            },
            {
                validFuelTariffValues,
                vehicleClass: vehicleClassByName[vehicleType] || null,
            },
        );

        try {
            if (isEditMode) {
                const result = await APIUpdateVehicleAdminPreset(token, setLoading, agencyId, payload);
                if (result?.data?.status !== 200) {
                    throw result;
                }
            } else {
                const result = await APICreateVehicleAdminPreset(token, setLoading, agencyId, payload);
                if (result?.data?.status !== 200) {
                    throw result;
                }
            }

            setLoading(false);
            navigate('/inventory/vehicleconfigurator', {
                state: {
                    snackbarStatus: 'success',
                    snackbarTitle: 'Success',
                    snackbarSub: isEditMode ? 'Vehicle Policy Updated!' : 'New Vehicle Policy Added!',
                },
            });
        } catch (err) {
            setLoading(false);
            const response = err?.response;
            const data = response?.data;
            const message = data?.error?.message
                || data?.message
                || err?.message;
            setSNErr(true);
            setSNErrMessage(message || 'Failed to save vehicle preset. Please try again.');
        }
    };

    const handleSave = () => {
        setSNErr(false);
        setSNErrMessage('');
        if (!requiredFormCompleted) {
            setSNErr(true);
            setSNErrMessage('Please fill in the vehicle preset name and select a vehicle type.');
            return;
        }
        handleCreateOrUpdatePolicy();
    };

    const assetNameInputStyle = snErr ? { border: '0.063rem solid #FDA29B' } : undefined;

    const handleDelete = () => {
        setDeleteModal(true);
    };

    const handleDeletePolicy = async () => {
        if (!isEditMode || !currentPolicyId) {
            return;
        }
        try {
            const result = await APIDeleteVehicleAdminPresets(token, setLoading, agencyId, {
                oid: [currentPolicyId],
            });
            if (result?.data?.status !== 200) {
                throw result;
            }
            setDeleteModal(false);
            setLoading(false);
            navigate('/inventory/vehicleconfigurator', {
                state: {
                    snackbarStatus: 'success',
                    snackbarTitle: 'Success',
                    snackbarSub: 'Vehicle Policy Deleted!',
                },
            });
        } catch (err) {
            setDeleteModal(false);
            setLoading(false);
            setSNErr(true);
            setSNErrMessage('Failed to delete vehicle preset. Please try again.');
        }
    };

    return (
        <div className="add-device-container">
            <div className="add-device-header-section">
                <div className="add-device-header-container">
                    <BackButton label="Back to vehicle configurator" btnClickBack={() => handleBackButton()} color="#FFFFFF" />
                </div>
            </div>
            <div className="add-device-form-section">
                <div className="add-device-form-container">
                    <div className="add-device-form-header">
                        <div className="add-device-form-header-content">
                            <Title title={isEditMode ? 'Edit Vehicle Configuration' : 'Add Vehicle Configuration'} deleteSubtitle titleSize="18px" lineHeight="28px" />
                        </div>
                        <ColoredLine />
                    </div>

                    <div className="add-device-form-content">
                        {snErr && SNErrMessage && (
                            <div className="vehicle-config-inline-error">{SNErrMessage}</div>
                        )}
                        <VehiclePresetFormGrid
                          presetName={presetName}
                          setPresetName={setPresetName}
                          vehicleType={vehicleType}
                          setVehicleType={setVehicleType}
                          descriptionValue={descriptionValue}
                          setDescriptionValue={setDescriptionValue}
                          fieldValues={fieldValues}
                          onFieldChange={handleFieldChange}
                          onFieldValuesPatch={handleFieldValuesPatch}
                          vehicleTypeInputStyle={assetNameInputStyle}
                          fuelPolicyOptions={fuelPolicyOptions}
                          selectedFuelPolicy={selectedFuelPolicy}
                          onFuelPolicyChange={handleFuelPolicyChange}
                          batteryPolicyOptions={batteryPolicyOptions}
                          selectedBatteryPolicy={selectedBatteryPolicy}
                          onBatteryPolicyChange={setSelectedBatteryPolicy}
                          fuelTariffOptions={fuelTariffOptions}
                          registryPriceByValue={registryPriceByValue}
                          registryOptionsLoading={registryOptionsLoading}
                          vehicleTypeOptions={vehicleTypeOptions}
                          vehicleTypesLoading={vehicleTypesLoading}
                          vehicleClassByName={vehicleClassByName}
                          canManageVehicleTypes={canManageVehicleTypes}
                          onAddVehicleType={addVehicleType}
                        />
                    </div>

                    <div className="add-device-footer">
                        <div className="add-device-button-container">
                            <SecondaryButton label="Cancel" onClickFunction={() => handleBackButton()} />
                            {isEditMode && (
                                <SecondaryButton label="Delete" onClickFunction={handleDelete} />
                            )}
                            <PrimaryButton label={isEditMode ? 'Update' : 'Save'} onClickFunction={handleSave} disable={loading} />
                        </div>
                    </div>
                </div>
            </div>
            {
                deleteModal && (
                    <div className="modal-container-table-bg">
                        <Modal
                          setSwitchModalShow={setDeleteModal}
                          switchModalShow={deleteModal}
                          btnConfirm={handleDeletePolicy}
                          module="DeleteModal"
                          source="VehiclePolicy"
                          dataRow={state}
                        />
                    </div>
                )
            }
        </div>
    );
};

export default AddVehicleConfig;
